import { useQuery } from '@tanstack/react-query'
import { BookOpen, RotateCcw, Save } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { errorMessage } from '@/api/errors'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'
import { Switch } from '@/components/ui/Switch'
import { useAuth } from '@/features/auth/useAuth'
import { cn } from '@/utils/cn'
import { formatNumber } from '@/utils/format'
import { subjectsQuery, useSyncClassSubjects } from '../api'
import type { SchoolClass, Subject } from '../types'

interface Row {
  included: boolean
  isOptional: boolean
  weeklyPeriods: string
  coefficient: string
}

type Draft = Record<number, Row>

function initialDraft(schoolClass: SchoolClass): Draft {
  const draft: Draft = {}
  for (const s of schoolClass.subjects) {
    draft[s.subjectId] = {
      included: true,
      isOptional: s.isOptional,
      weeklyPeriods: s.weeklyPeriods?.toString() ?? '',
      coefficient: String(s.coefficient ?? 1),
    }
  }
  return draft
}

const emptyRow: Row = { included: false, isOptional: false, weeklyPeriods: '', coefficient: '1' }
const validPeriods = (v: string) => v === '' || (/^\d+$/.test(v) && Number(v) <= 60)
const parseCoefficient = (v: string) => Number(v.trim().replace(',', '.'))
const validCoefficient = (v: string) => /^\d{1,2}([.,]\d{1,2})?$/.test(v.trim()) && parseCoefficient(v) <= 20

/** Serialised payload, used both for saving and for dirty-checking. */
function toPayload(draft: Draft, subjects: Subject[]) {
  return subjects
    .filter((s) => draft[s.id]?.included)
    .map((s) => {
      const row = draft[s.id] ?? emptyRow
      return {
        subjectId: s.id,
        isOptional: row.isOptional,
        weeklyPeriods: row.weeklyPeriods === '' ? null : Number(row.weeklyPeriods),
        coefficient: validCoefficient(row.coefficient) ? parseCoefficient(row.coefficient) : 1,
      }
    })
}

/**
 * Checklist of every school subject with per-class options. Mount with a `key` derived from the
 * class's saved subjects so the draft resets whenever the server copy changes.
 */
export function CurriculumEditor({ schoolClass }: { schoolClass: SchoolClass }) {
  const { t } = useTranslation('academic')
  const subjects = useQuery(subjectsQuery())
  const sync = useSyncClassSubjects()
  const { can } = useAuth()
  const readOnly = !can('academic.manage')
  const [draft, setDraft] = useState<Draft>(() => initialDraft(schoolClass))

  const all = useMemo(() => subjects.data ?? [], [subjects.data])
  const payload = useMemo(() => toPayload(draft, all), [draft, all])
  const initialPayload = useMemo(() => toPayload(initialDraft(schoolClass), all), [schoolClass, all])
  const dirty = JSON.stringify(payload) !== JSON.stringify(initialPayload)
  const periodsInvalid = all.some(
    (s) => draft[s.id]?.included && !validPeriods(draft[s.id]?.weeklyPeriods ?? ''),
  )
  const coefficientInvalid = all.some(
    (s) => draft[s.id]?.included && !validCoefficient(draft[s.id]?.coefficient ?? ''),
  )
  const invalid = periodsInvalid || coefficientInvalid
  const totalPeriods = payload.reduce((sum, p) => sum + (p.weeklyPeriods ?? 0), 0)
  const totalCoefficients = payload.reduce((sum, p) => sum + p.coefficient, 0)

  const patch = (id: number, change: Partial<Row>) =>
    setDraft((d) => ({ ...d, [id]: { ...(d[id] ?? emptyRow), ...change } }))

  const save = () =>
    sync.mutate(
      { classId: schoolClass.id, subjects: payload },
      {
        onSuccess: () =>
          toast.success(
            t('Curriculum saved'),
            t('{{name}} now has {{count}} subjects.', { name: schoolClass.name, count: payload.length }),
          ),
        onError: (e) => toast.error(t('Could not save the curriculum'), errorMessage(e)),
      },
    )

  if (subjects.isPending) return <LoadingState />
  if (subjects.error) return <ErrorState onRetry={() => void subjects.refetch()} compact />
  if (all.length === 0) {
    return (
      <EmptyState
        icon={<BookOpen />}
        title={t('No subjects defined')}
        description={
          <Trans
            t={t}
            i18nKey="Create subjects first on the <link>Subjects</link> page."
            components={{
              link: (
                <Link to="/admin/academic/subjects" className="font-medium text-brand-600 hover:underline" />
              ),
            }}
          />
        }
        compact
      />
    )
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted" aria-live="polite">
          <Trans
            t={t}
            i18nKey="<b>{{selected}}</b> of {{total}} subjects · <b>{{periods}}</b> periods per week"
            values={{ selected: payload.length, total: all.length, periods: totalPeriods }}
            components={{ b: <span className="font-medium text-ink tabular" /> }}
          />{' '}
          ·{' '}
          <Trans
            t={t}
            i18nKey="Total coefficients <b>{{total}}</b>"
            values={{ total: formatNumber(totalCoefficients) }}
            components={{ b: <span className="font-medium text-ink tabular" /> }}
          />
        </p>
        {!readOnly ? (
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<RotateCcw />}
              disabled={!dirty || sync.isPending}
              onClick={() => setDraft(initialDraft(schoolClass))}
            >
              {t('Reset')}
            </Button>
            <Button
              size="sm"
              leftIcon={<Save />}
              disabled={!dirty || invalid}
              loading={sync.isPending}
              onClick={save}
            >
              {t('Save curriculum')}
            </Button>
          </div>
        ) : null}
      </div>

      <ul
        className="divide-y divide-line overflow-hidden rounded-card border border-line"
        aria-label={t('Subjects')}
      >
        {all.map((s) => {
          const row = draft[s.id] ?? emptyRow
          const rowPeriodsInvalid = row.included && !validPeriods(row.weeklyPeriods)
          const rowCoefficientInvalid = row.included && !validCoefficient(row.coefficient)
          return (
            <li
              key={s.id}
              className={cn(
                'flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center',
                row.included ? 'bg-surface' : 'bg-surface-muted/60',
              )}
            >
              <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                <Checkbox
                  checked={row.included}
                  disabled={readOnly}
                  onCheckedChange={(c) => patch(s.id, { included: c === true })}
                  aria-label={t('Include {{name}}', { name: s.name })}
                />
                <span
                  className="size-3 shrink-0 rounded-full ring-1 ring-line ring-inset"
                  style={{ backgroundColor: s.color ?? 'transparent' }}
                  aria-hidden="true"
                />
                <span className="min-w-0">
                  <span
                    className={cn('block truncate font-medium', row.included ? 'text-ink' : 'text-muted')}
                  >
                    {s.name}
                  </span>
                  {s.code ? <span className="block font-mono text-2xs text-muted">{s.code}</span> : null}
                </span>
              </label>
              <div className="flex items-center gap-5 pl-7 sm:pl-0">
                <div className="flex items-center gap-2 text-xs text-ink-soft">
                  <Switch
                    id={`curriculum-optional-${s.id}`}
                    checked={row.isOptional}
                    disabled={readOnly || !row.included}
                    onCheckedChange={(c) => patch(s.id, { isOptional: c })}
                    aria-label={t('{{name}} is optional', { name: s.name })}
                  />
                  <label htmlFor={`curriculum-optional-${s.id}`} aria-hidden="true">
                    {t('Optional')}
                  </label>
                </div>
                <div className="flex items-center gap-2 text-xs text-ink-soft">
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={60}
                    size="sm"
                    className="w-16 text-right tabular"
                    value={row.weeklyPeriods}
                    disabled={readOnly || !row.included}
                    aria-invalid={rowPeriodsInvalid || undefined}
                    aria-label={t('Weekly periods for {{name}}', { name: s.name })}
                    onChange={(e) => patch(s.id, { weeklyPeriods: e.target.value })}
                  />
                  <span aria-hidden="true">{t('/ week')}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-ink-soft">
                  <span aria-hidden="true">{t('Coef.')}</span>
                  <Input
                    inputMode="decimal"
                    size="sm"
                    className="w-16 text-right tabular"
                    value={row.coefficient}
                    disabled={readOnly || !row.included}
                    aria-invalid={rowCoefficientInvalid || undefined}
                    aria-label={t('Coefficient of {{name}}', { name: s.name })}
                    title={t('Weight of the subject in the averages')}
                    onChange={(e) => patch(s.id, { coefficient: e.target.value })}
                  />
                </div>
              </div>
            </li>
          )
        })}
      </ul>
      {periodsInvalid ? (
        <p className="mt-2 text-xs font-medium text-danger-600" role="alert">
          {t('Weekly periods must be a whole number between 0 and 60.')}
        </p>
      ) : null}
      {coefficientInvalid ? (
        <p className="mt-2 text-xs font-medium text-danger-600" role="alert">
          {t('A coefficient is a number between 0 and 20, e.g. 2 or 1.5.')}
        </p>
      ) : null}
    </div>
  )
}
